import Cookies from "js-cookie";
import { throttle } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { isChrome } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { Box, Flex } from "rebass";
import { TabMenu } from "../components";
import BannerWrapper from "../components/AlertBanner/components/BannersWrapper/BannersWrapper";
import useResponsive from "../hooks/useResponsive";
import { layoutActions, systemActions } from "../logic/actions";
import { Footer, Header } from "../sections";
import { theme } from "../utils";
import config from "../utils/globalConfig";

const MainLayout = ({
  client,
  children,
  scrollDetect,
  token,
  viewportAll = false,
  templateBar = false,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const { messages } = useSelector((state) => state.yodaMessages);
  const { scrolledToBottom } = useSelector((state) => state.layout);
  const { minimizedUi } = useSelector((state) => state.system);
  const { isMobile } = useResponsive();

  const [messageState, setMessageState] = useState([]);

  const dispatch = useDispatch();
  const headerBlockRef = useRef();

  const layoutContainerRef = useRef();

  function scrollHandle(e) {
    if (!scrollDetect) return;
    scrollDetect(e);
  }

  useEffect(() => {
    if (layoutContainerRef && layoutContainerRef.current) {
      const element = layoutContainerRef?.current;
      const handleContainerScroll = throttle(() => {
        if (element) {
          const bottomOffset = Math.round(
            element.scrollHeight - element.scrollTop,
          );
          const bottom =
            bottomOffset >= Math.round(element.clientHeight) - 1 &&
            bottomOffset <= Math.round(element.clientHeight) + 1;
          if (scrolledToBottom !== bottom) {
            dispatch(layoutActions.setScrolledToBottom(bottom));
          }
        }
      }, 200);
      element.addEventListener("scroll", handleContainerScroll);
      return () => {
        element.removeEventListener("scroll", handleContainerScroll);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutContainerRef, scrolledToBottom]);

  useEffect(() => {
    if (messages?.length > 0) {
      let newMessages = messages
        ?.map((message) => {
          const isDisabled = Cookies.get(`B2BHerno_Banner_${message?.id}`);
          return { ...message, closed: isDisabled };
        })
        ?.sort((a, b) => {
          if (a.priority < b.priority) return -1;
          if (a.priority > b.priority) return 1;

          return 0;
        });

      setMessageState(newMessages);
    } else {
      setMessageState([]);
    }
  }, [messages]);

  useEffect(() => {
    if (headerBlockRef?.current) {
      dispatch(systemActions.setOffsetTop(headerBlockRef.current.clientHeight));
    }
  }, [dispatch, messageState, minimizedUi]);

  function updateMessages(id) {
    let message = messageState.filter((message) => {
      if (message?.id !== id) return message;
    });

    setMessageState(message);
  }

  function setCookies(id) {
    Cookies.set(`B2BHerno_Banner_${id}`, true, { expires: 365 });
    updateMessages(id);
  }

  const renderLayout = () => {
    return (
      <Flex
        width={1}
        flexDirection="column-reverse"
        height={["100%", "100dvh"]}
        minHeight={"100dvh"}
        bg={theme.backgroundPrimary}
        overflow={["unset", "hidden"]}
      >
        <Flex
          id={"scrollContainer"}
          ref={layoutContainerRef}
          flexGrow={1}
          justifyContent="center"
          onWheel={scrollHandle}
          {...(!isMobile && {
            overflowY: isChrome ? "overlay" : "scroll",
            overflowX: null,
          })}
        >
          <Flex width={1} flexDirection="column">
            <Flex
              width={[1, null, null, 1]}
              maxWidth={!viewportAll ? config.VIEWPORT : "100%"}
              flexDirection="column"
              style={{
                marginLeft: "auto",
                marginRight: "auto",
              }}
              flexGrow={1}
            >
              {children}
            </Flex>
            {!minimizedUi && <Footer />}
          </Flex>
        </Flex>
        <Box
          ref={headerBlockRef}
          sx={{
            width: "100%",
            position: ["sticky", "initial"],
            top: [0, "unset"],
            left: [0, "unset"],
            zIndex: 2,
          }}
        >
          <Header
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            token={token}
          />
          {templateBar}
          {!minimizedUi && !templateBar && (
            <>
              <BannerWrapper banners={messageState} cookieUpdate={setCookies} />
              <TabMenu client={client} />
            </>
          )}
        </Box>
        <ReactTooltip
          id="main-tooltip"
          opacity="1"
          delayShow={500}
          style={{
            background: "#545454",
            fontSize: "12px",
            padding: "4px 8px",
            zIndex: 9998,
          }}
        />
      </Flex>
    );
  };

  return renderLayout();
};

export { MainLayout };
